import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EM_DASH,
  formatDate,
  formatNumber,
  formatUtcDayTime,
  HFBox,
  HFButton,
  HFCard,
  HFChip,
  HFDataTable,
  HFModal,
  HFSelect,
  HFTextField,
  HFTooltip,
  type HFDataTableRowTree,
  type HFGridColDef,
} from '@hf/designSystem/v1';
import {
  OMS_FILL_COLUMN_IDS,
  OMS_LAYOUT_COLUMN_MAX_WIDTH,
  OMS_LAYOUT_COLUMN_MIN_WIDTH,
  omsFillListSchema,
  type OmsFill,
  type OmsFillAllocation,
  type OmsFillColumnId,
  type OmsFillFilters,
  type OmsFillGroupBy,
  type OmsFillList,
  type OmsLayoutColumn,
  type OmsLayoutSort,
  type OmsSide,
} from '@hf/sharedContract';
import { GA, track } from '@core/utils/analytics';
import { csvFileName, downloadCsv, toCsv } from '@core/utils/csv';
import { useHttp } from '@core/utils/http';
import { loadFillLayout, queueFillLayoutSave, syncFillLayout } from '../layout/fillLayoutWrite';
import { monitorPaneSx } from '../layout/monitorPane';
import { ColumnVisibilityList } from './ColumnVisibilityList';
import { PanelHeader } from './PanelHeader';
import { TableSkeleton } from './TableSkeleton';

type FillRow = {
  kind: 'fill' | 'group';
  id: string;
  tradeTime: string;
  execId: string;
  parent: string;
  exchange: string;
  symbol: string;
  side: OmsSide | '';
  qty: number | null;
  qtyLabel?: string;
  price: number | null;
  currency: string;
  marketValue: number | null;
  strategy: string;
  clearingAccount: string | null;
  importerStatus: string;
  children?: FillRow[];
};

type Facets = { books: string[]; exchanges: string[]; products: string[] };

type PageMark = { cursor?: string; start: number };

const FILTERS: Array<{ id: OmsFillAllocation; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'allocated', label: 'Allocated' },
  { id: 'unallocated', label: 'Unallocated' },
];

const GROUPS: Array<{ id: OmsFillGroupBy; label: string }> = [
  { id: 'none', label: 'None' },
  { id: 'order', label: 'Order' },
  { id: 'product', label: 'Product' },
];

const COMPACT_BUTTON_SX = { minHeight: 28, px: 1.25, py: 0.25, fontSize: 12 } as const;
/** 32px fields. The shrunk label sits on the outline and the value stays clear of the arrow. */
const FILTER_FIELD_SX = {
  '& .MuiInputBase-root': { height: 32, fontSize: 13 },
  '& .MuiInputBase-input': { py: 0, pl: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { transform: 'translate(12px, 7px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(12px, -7px) scale(0.75)' },
  '& .MuiSelect-icon': { right: 6, fontSize: 18 },
} as const;
const CHROME_SX = { display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, containerType: 'inline-size' } as const;
const CHROME_ROW_SX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' } as const;
const CLUSTER_SX = { display: 'flex', alignItems: 'center', gap: 1 } as const;
const FILTER_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))',
  columnGap: 1,
  rowGap: 2,
  alignItems: 'center',
  '@container (max-width: 1052px)': { gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))' },
  '@container (max-width: 600px)': { gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))' },
  '@container (max-width: 300px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
} as const;
const GRID_CELL_SX = { display: 'flex', width: '100%' } as const;
const HEADER_SX = {
  fontWeight: 800,
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;
const EMPTY_FACETS: Facets = { books: [], exchanges: [], products: [] };
const NOT_READY = 'Not implemented yet.';

function lotLine(bought: number, sold: number, unsided: number): string {
  const parts = [bought > 0 ? `Bought ${bought}` : '', sold > 0 ? `Sold ${sold}` : '', unsided > 0 ? `No side ${unsided}` : ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Qty 0';
}
const PAGE_SIZES = ['50', '100', '150', '200'] as const;

function fillsPath(query: {
  date?: string;
  allocation: OmsFillAllocation;
  book: string;
  exchange: string;
  product: string;
  side: string;
  q: string;
  cursor?: string;
  pageSize: (typeof PAGE_SIZES)[number];
  groupBy: OmsFillGroupBy;
}): string {
  const params = new URLSearchParams();
  if (query.date) params.set('date', query.date);
  if (query.allocation !== 'all') params.set('allocation', query.allocation);
  if (query.book) params.set('book', query.book);
  if (query.exchange) params.set('exchange', query.exchange);
  if (query.product) params.set('product', query.product);
  if (query.side) params.set('side', query.side);
  if (query.q.trim()) params.set('q', query.q.trim());
  if (query.pageSize !== '50') params.set('pageSize', query.pageSize);
  if (query.groupBy !== 'none') params.set('group', query.groupBy);
  if (query.cursor) params.set('cursor', query.cursor);
  const qs = params.toString();
  return `/oms/fills${qs ? `?${qs}` : ''}`;
}

function toFillRow(fill: OmsFill): FillRow {
  return {
    kind: 'fill',
    id: fill.execId,
    tradeTime: formatUtcDayTime(fill.tradeTime),
    execId: fill.execId,
    parent: String(fill.orderId),
    exchange: fill.exchange ?? '',
    symbol: fill.bbFullSymbol,
    side: fill.side ?? '',
    qty: fill.qty,
    price: fill.price,
    currency: fill.currency ?? '',
    marketValue: fill.marketValue ?? null,
    strategy: fill.strategy ?? '',
    clearingAccount: fill.clearingAccount,
    importerStatus: fill.importerStatus ?? EM_DASH,
  };
}

function sameText(rows: FillRow[], field: 'symbol' | 'side'): string {
  const first = rows[0]?.[field] ?? '';
  return rows.every((row) => row[field] === first) ? first : '';
}

function groupPage(fills: OmsFill[], groupBy: Exclude<OmsFillGroupBy, 'none'>): FillRow[] {
  const buckets = new Map<string, OmsFill[]>();
  for (const fill of fills) {
    const key = groupBy === 'order' ? String(fill.orderId) : (fill.product ?? '');
    const bucket = buckets.get(key) ?? [];
    bucket.push(fill);
    buckets.set(key, bucket);
  }
  return [...buckets.entries()].map(([key, bucket]) => {
    const children = bucket.map(toFillRow);
    let bought = 0;
    let sold = 0;
    let unsided = 0;
    for (const fill of bucket) {
      if (fill.side === 'BUY') bought += fill.qty;
      else if (fill.side === 'SELL') sold += fill.qty;
      else unsided += fill.qty;
    }
    const parts = [bought > 0 ? `B ${bought}` : '', sold > 0 ? `S ${sold}` : '', unsided > 0 ? `No side ${unsided}` : ''].filter(Boolean);
    const currency = bucket[0]?.currency;
    const valued = Boolean(currency) && bucket.every((fill) => fill.currency === currency && fill.marketValue != null);
    const marketValue = valued ? bucket.reduce((sum, fill) => sum + (fill.marketValue ?? 0), 0) : null;
    const count = bucket.length === 1 ? '1 fill' : `${bucket.length} fills`;
    return {
      kind: 'group' as const,
      id: groupBy === 'order' ? `group:order:${key}` : `group:product:${key || 'none'}`,
      tradeTime: groupBy === 'order' ? `Order ${key} · ${count}` : key || 'No product',
      execId: '',
      parent: '',
      exchange: '',
      symbol: sameText(children, 'symbol'),
      side: sameText(children, 'side') as OmsSide | '',
      qty: parts.length === 1 ? bought + sold + unsided : null,
      qtyLabel: parts.length > 1 ? parts.join(' · ') : undefined,
      price: null,
      currency: '',
      marketValue,
      strategy: '',
      clearingAccount: null,
      importerStatus: '',
      children,
    };
  });
}

function Header({ label }: { label: string }) {
  return (
    <HFBox component="span" sx={HEADER_SX}>
      {label}
    </HFBox>
  );
}

function SideCell({ side }: { side: OmsSide | '' }) {
  if (!side) return null;
  return (
    <HFBox component="span" sx={{ fontWeight: 800, color: side === 'BUY' ? 'success.main' : 'error.main' }}>
      {side}
    </HFBox>
  );
}

const FILL_COLUMN_DETAILS: Record<OmsFillColumnId, { label: string; width: number; align?: 'right' }> = {
  tradeTime: { label: 'Trade time', width: 220 },
  execId: { label: 'Exec id', width: 130 },
  parent: { label: 'Parent', width: 90 },
  exchange: { label: 'Exchange', width: 100 },
  symbol: { label: 'Symbol', width: 150 },
  side: { label: 'Side', width: 72 },
  qty: { label: 'Qty', width: 72, align: 'right' },
  price: { label: 'Price', width: 90, align: 'right' },
  currency: { label: 'Currency', width: 90 },
  marketValue: { label: 'Market value', width: 120, align: 'right' },
  strategy: { label: 'Strategy', width: 110 },
  clearingAccount: { label: 'Clearing account', width: 220 },
  importerStatus: { label: 'Importer', width: 100 },
};

function defaultFillColumns(): OmsLayoutColumn[] {
  return OMS_FILL_COLUMN_IDS.map((id) => ({ id, width: FILL_COLUMN_DETAILS[id].width, visible: true }));
}

function sameColumns(left: OmsLayoutColumn[], right: OmsLayoutColumn[]): boolean {
  return (
    left.length === right.length &&
    left.every((column, index) => column.id === right[index]?.id && column.width === right[index]?.width && column.visible === right[index]?.visible)
  );
}

function sameSort(left: OmsLayoutSort | null, right: OmsLayoutSort | null): boolean {
  return left?.id === right?.id && left?.direction === right?.direction;
}

function applyFieldOrder(columns: OmsLayoutColumn[], fields: string[]): OmsLayoutColumn[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const next = fields.map((id) => byId.get(id)).filter((column): column is OmsLayoutColumn => column != null);
  const seen = new Set(next.map((column) => column.id));
  for (const column of columns) {
    if (!seen.has(column.id)) next.push(column);
  }
  return next;
}

function applySavedFillColumns(saved: OmsLayoutColumn[]): OmsLayoutColumn[] {
  const defaults = defaultFillColumns();
  const known = new Map(defaults.map((column) => [column.id, column]));
  const used = new Set<string>();
  const next: OmsLayoutColumn[] = [];
  for (const column of saved) {
    const base = known.get(column.id);
    if (!base || used.has(column.id)) continue;
    used.add(column.id);
    next.push({ id: column.id, width: column.width ?? base.width, visible: column.visible });
  }
  for (const column of defaults) {
    if (!used.has(column.id)) next.push(column);
  }
  return next;
}

function column(field: OmsFillColumnId, width: number): HFGridColDef<FillRow> {
  const details = FILL_COLUMN_DETAILS[field];
  return {
    field,
    headerName: details.label,
    width,
    minWidth: OMS_LAYOUT_COLUMN_MIN_WIDTH,
    maxWidth: OMS_LAYOUT_COLUMN_MAX_WIDTH,
    sortable: true,
    align: details.align,
    headerAlign: details.align,
    renderHeader: () => <Header label={details.label} />,
  };
}

function gridColumn(saved: OmsLayoutColumn): HFGridColDef<FillRow> {
  const id = saved.id as OmsFillColumnId;
  const base = column(id, saved.width ?? FILL_COLUMN_DETAILS[id].width);
  if (id === 'side') return { ...base, renderCell: ({ row }) => <SideCell side={row.side} /> };
  if (id === 'qty') {
    return {
      ...base,
      type: 'number',
      valueFormatter: (value: number | null) => (value == null ? '' : String(value)),
      renderCell: ({ row, value }) => row.qtyLabel ?? (value == null ? '' : String(value)),
    };
  }
  if (id === 'price') return { ...base, type: 'number', valueFormatter: (value: number | null) => (value == null ? '' : value.toFixed(2)) };
  if (id === 'marketValue') {
    return { ...base, type: 'number', valueFormatter: (value: number | null) => (value == null ? '' : formatNumber(value, 2)) };
  }
  if (id === 'clearingAccount') {
    return {
      ...base,
      renderCell: ({ row }) => {
        if (row.kind === 'group') return null;
        return row.clearingAccount ? row.clearingAccount : <HFChip tone="warn" label="Unallocated" />;
      },
    };
  }
  if (id === 'importerStatus') {
    return {
      ...base,
      renderCell: ({ row }) =>
        row.importerStatus === EM_DASH ? (
          <HFBox component="span" sx={{ color: 'text.secondary' }}>
            {EM_DASH}
          </HFBox>
        ) : (
          row.importerStatus
        ),
    };
  }
  return base;
}

/** Nulls last, whichever way the column is sorted. */
function compareFills(left: FillRow, right: FillRow, sort: OmsLayoutSort): number {
  const a = left[sort.id as OmsFillColumnId];
  const b = right[sort.id as OmsFillColumnId];
  if (a == null || b == null) return Number(a == null) - Number(b == null);
  const order =
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return sort.direction === 'desc' ? -order : order;
}

function csvValue(row: FillRow, id: OmsFillColumnId): string {
  switch (id) {
    case 'price':
    case 'marketValue':
      return row[id] == null ? '' : row[id].toFixed(2);
    case 'qty':
      return row.qty == null ? '' : String(row.qty);
    case 'clearingAccount':
      return row.clearingAccount || 'Unallocated';
    default:
      return row[id];
  }
}

function options(values: string[]): Array<{ value: string; label: string }> {
  return [{ value: '', label: 'All' }, ...values.map((value) => ({ value, label: value }))];
}

/** A saved filter that today's facets lack still shows, so the select does not blank out. */
function facetOptions(loading: boolean, values: string[], current: string): Array<{ value: string; label: string }> {
  return loading ? [{ value: '', label: 'Loading…' }] : options(current && !values.includes(current) ? [...values, current] : values);
}

function DisabledSelect({ label, title }: { label: string; title: string }) {
  return (
    <HFTooltip title={title}>
      <HFBox component="span" sx={GRID_CELL_SX}>
        <HFSelect label={label} size="small" disabled displayEmpty value="" options={[{ value: '', label: 'All' }]} formControlSx={FILTER_FIELD_SX} />
      </HFBox>
    </HFTooltip>
  );
}

function EmptyFills({ message }: { message: string }) {
  return <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>{message}</HFBox>;
}

/** Read-only fills for the trade day. Filters that the order read can answer run on the server. */
export function TradeBlotter({
  date,
  onRead,
  compact = false,
  initialFilters,
  onFiltersChange,
  initialColumns,
  initialSort,
  onColumnsChange,
}: {
  date?: string;
  /** Called with whether each fills read succeeded, for the tab-bar status. */ onRead?: (ok: boolean) => void;
  /** Inside the Custom layout: a one-line header, with the toolbar behind a Filters toggle. */
  compact?: boolean;
  /** Read once, when the blotter mounts. */
  initialFilters?: OmsFillFilters;
  onFiltersChange?: (filters: OmsFillFilters) => void;
  /** Compact only, read once. The Custom layout keeps the columns and never touches the personal layout. */
  initialColumns?: OmsLayoutColumn[];
  initialSort?: OmsLayoutSort;
  onColumnsChange?: (columns: OmsLayoutColumn[], sort?: OmsLayoutSort) => void;
}) {
  const { request } = useHttp();
  const [allocation, setAllocation] = useState<OmsFillAllocation>(initialFilters?.allocation ?? 'all');
  const [book, setBook] = useState(initialFilters?.book ?? '');
  const [exchange, setExchange] = useState(initialFilters?.exchange ?? '');
  const [product, setProduct] = useState(initialFilters?.product ?? '');
  const [side, setSide] = useState<string>(initialFilters?.side ?? '');
  const [search, setSearch] = useState(initialFilters?.q ?? '');
  const [q, setQ] = useState(initialFilters?.q ?? '');
  const [groupBy, setGroupBy] = useState<OmsFillGroupBy>(initialFilters?.groupBy ?? 'none');
  const [chromeOpen, setChromeOpen] = useState(false);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>('50');
  const [layoutColumns, setLayoutColumns] = useState<OmsLayoutColumn[]>(() =>
    compact && initialColumns ? applySavedFillColumns(initialColumns) : defaultFillColumns(),
  );
  const [sort, setSort] = useState<OmsLayoutSort | null>(() =>
    compact && initialColumns && initialSort && (OMS_FILL_COLUMN_IDS as readonly string[]).includes(initialSort.id) ? initialSort : null,
  );
  const [layoutReady, setLayoutReady] = useState(compact);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OmsLayoutColumn[]>(defaultFillColumns);
  const [exporting, setExporting] = useState(false);
  const layoutColumnsRef = useRef(layoutColumns);
  const sortRef = useRef(sort);
  const requestRef = useRef(request);
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  layoutColumnsRef.current = layoutColumns;
  sortRef.current = sort;
  requestRef.current = request;
  const [pages, setPages] = useState<PageMark[]>([{ start: 1 }]);
  const [fills, setFills] = useState<OmsFill[] | null>(null);
  const [total, setTotal] = useState(0);
  const [boughtQty, setBoughtQty] = useState(0);
  const [soldQty, setSoldQty] = useState(0);
  const [unsidedQty, setUnsidedQty] = useState(0);
  const [marketValueTotal, setMarketValueTotal] = useState<number | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [listsReady, setListsReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const generation = useRef(0);
  const page = pages[pages.length - 1] ?? { start: 1 };

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setListsReady(false);
  }, [date]);

  useEffect(() => {
    if (compact) return;
    let cancelled = false;
    const layout = loadFillLayout(request);
    void layout.result.then((stored) => {
      if (cancelled) return;
      syncFillLayout(stored);
      setLayoutColumns(applySavedFillColumns(stored?.columns ?? []));
      const savedSort = stored?.sort;
      setSort(savedSort && (OMS_FILL_COLUMN_IDS as readonly string[]).includes(savedSort.id) ? savedSort : null);
      setGroupBy(stored?.fillGroupBy ?? 'none');
      setLayoutReady(true);
    });
    return () => {
      cancelled = true;
      layout.cancel();
    };
  }, [compact, request]);

  useEffect(() => {
    setPages([{ start: 1 }]);
  }, [date, allocation, book, exchange, product, side, q, groupBy, pageSize]);

  useEffect(() => {
    if (!layoutReady) return;
    const current = ++generation.current;
    const paging = Boolean(page.cursor);
    if (!paging) {
      setFills(null);
      setFailed(false);
    } else {
      setPageLoading(true);
    }
    let cancelled = false;
    void request<OmsFillList>(fillsPath({ date, allocation, book, exchange, product, side, q, cursor: page.cursor, pageSize, groupBy }), {
      responseSchema: omsFillListSchema,
    }).then((result) => {
      if (cancelled || generation.current !== current) return;
      setPageLoading(false);
      if (result.status !== 200 || !result.data || !Array.isArray(result.data.fills)) {
        setListsReady(true);
        setFills([]);
        setNextCursor(undefined);
        setFailed(true);
        onReadRef.current?.(false);
        return;
      }
      setFills(result.data.fills);
      setTotal(result.data.total);
      setBoughtQty(result.data.boughtQty);
      setSoldQty(result.data.soldQty);
      setUnsidedQty(result.data.unsidedQty);
      setMarketValueTotal(result.data.marketValueTotal);
      setNextCursor(result.data.nextCursor);
      setFacets(result.data.facets);
      setListsReady(true);
      setFailed(false);
      onReadRef.current?.(true);
    });
    return () => {
      cancelled = true;
    };
  }, [date, allocation, book, exchange, product, side, q, page.cursor, pageSize, groupBy, layoutReady, request]);

  const grouped = useMemo(() => (fills && groupBy !== 'none' ? groupPage(fills, groupBy) : null), [fills, groupBy]);

  useEffect(() => {
    setExpanded(new Set(grouped?.map((row) => row.id) ?? []));
  }, [grouped]);

  const rows = grouped ?? (fills ?? []).map(toFillRow);
  const groupIds = grouped?.map((row) => row.id) ?? [];
  const allExpanded = groupIds.length > 0 && groupIds.every((id) => expanded.has(id));
  const gridColumns = useMemo(() => layoutColumns.map(gridColumn), [layoutColumns]);
  const visibility = useMemo(() => Object.fromEntries(layoutColumns.map((column) => [column.id, column.visible])), [layoutColumns]);
  const visibleDraft = draft.filter((column) => column.visible);
  const groupLabel = GROUPS.find((item) => item.id === groupBy)?.label ?? groupBy;

  const saveLayout = (
    nextColumns: OmsLayoutColumn[],
    nextSort: OmsLayoutSort | null,
    previousColumns: OmsLayoutColumn[],
    previousSort: OmsLayoutSort | null,
  ) => {
    const sortToStore = nextSort && nextColumns.some((column) => column.id === nextSort.id) ? nextSort : null;
    if (compact) {
      onColumnsChange?.(nextColumns, sortToStore ?? undefined);
      return;
    }
    void queueFillLayoutSave(requestRef.current, { columns: nextColumns, sort: sortToStore }).then((saved) => {
      if (saved) track(GA.savedOmsColumnLayout, { layout: 'blotter' });
      if (!saved && layoutColumnsRef.current === nextColumns) {
        layoutColumnsRef.current = previousColumns;
        sortRef.current = previousSort;
        setLayoutColumns(previousColumns);
        setSort(previousSort);
      }
    });
  };

  const onColumnsReorder = (fields: string[]) => {
    if (!layoutReady) return;
    const current = layoutColumnsRef.current;
    const next = applyFieldOrder(current, fields);
    if (sameColumns(next, current)) return;
    layoutColumnsRef.current = next;
    setLayoutColumns(next);
    saveLayout(next, sortRef.current, current, sortRef.current);
  };

  const onColumnWidthChange = ({ colDef, width }: { colDef: { field: string }; width: number }) => {
    if (!layoutReady) return;
    const current = layoutColumnsRef.current;
    const next = current.map((column) => (column.id === colDef.field ? { ...column, width } : column));
    if (sameColumns(next, current)) return;
    layoutColumnsRef.current = next;
    setLayoutColumns(next);
    saveLayout(next, sortRef.current, current, sortRef.current);
  };

  const onSortModelChange = (model: ReadonlyArray<{ field: string; sort?: string | null }>) => {
    if (!layoutReady) return;
    const picked = model.find((item) => item.sort === 'asc' || item.sort === 'desc');
    const nextSort: OmsLayoutSort | null = picked ? { id: picked.field, direction: picked.sort === 'desc' ? 'desc' : 'asc' } : null;
    if (sameSort(nextSort, sortRef.current)) return;
    const previousSort = sortRef.current;
    sortRef.current = nextSort;
    setSort(nextSort);
    saveLayout(layoutColumnsRef.current, nextSort, layoutColumnsRef.current, previousSort);
  };
  /** Outside the Custom layout the grouping is the user's own: it is saved with their columns and comes back on the next visit. */
  const chooseGroupBy = (next: OmsFillGroupBy) => {
    if (next === groupBy) return;
    setGroupBy(next);
    if (!compact) void queueFillLayoutSave(requestRef.current, { fillGroupBy: next });
  };

  const openEditor = () => {
    setDraft(layoutColumns.map((column) => ({ ...column })));
    setEditing(true);
  };

  const applyColumns = () => {
    const previousColumns = layoutColumnsRef.current;
    const previousSort = sortRef.current;
    const keptSort = previousSort && draft.some((column) => column.id === previousSort.id && column.visible) ? previousSort : null;
    layoutColumnsRef.current = draft;
    sortRef.current = keptSort;
    setLayoutColumns(draft);
    setSort(keptSort);
    setEditing(false);
    saveLayout(draft, keptSort, previousColumns, previousSort);
  };

  /** Every fill the filters match, on every page, flat and in the grid's sort. Group rows are not written. */
  const exportCsv = async () => {
    setExporting(true);
    const all: OmsFill[] = [];
    let cursor: string | undefined;
    do {
      const result = await requestRef.current<OmsFillList>(
        fillsPath({ date, allocation, book, exchange, product, side, q, cursor, pageSize: '200', groupBy: 'none' }),
        { responseSchema: omsFillListSchema, coalesce: 'url' },
      );
      if (result.status !== 200 || !result.data || !Array.isArray(result.data.fills)) {
        setExporting(false);
        return;
      }
      all.push(...result.data.fills);
      cursor = result.data.nextCursor;
    } while (cursor);
    const exported = all.map(toFillRow);
    if (sort) exported.sort((left, right) => compareFills(left, right, sort));
    const visible = layoutColumns.filter((column) => column.visible).map((column) => column.id as OmsFillColumnId);
    const header = visible.map((id) => FILL_COLUMN_DETAILS[id].label);
    downloadCsv(
      csvFileName('oms-trade-blotter', date),
      toCsv(
        header,
        exported.map((row) => visible.map((id) => csvValue(row, id))),
      ),
    );
    track(GA.exportedOmsGridCsv, { layout: 'fills', format: 'csv' });
    setExporting(false);
  };

  const filters = useMemo<OmsFillFilters>(
    () => ({
      allocation,
      groupBy,
      ...(book ? { book } : {}),
      ...(exchange ? { exchange } : {}),
      ...(product ? { product } : {}),
      ...(side ? { side: side as OmsSide } : {}),
      ...(q ? { q: q.slice(0, 64) } : {}),
    }),
    [allocation, book, exchange, groupBy, product, q, side],
  );
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  useEffect(() => onFiltersChangeRef.current?.(filters), [filters]);
  const summary = [
    date ? formatDate(date, 'medium') : 'Today',
    allocation !== 'all' ? FILTERS.find((item) => item.id === allocation)?.label : '',
    book,
    exchange,
    product,
    side,
    q ? `"${q}"` : '',
    groupBy !== 'none' ? `By ${groupLabel.toLowerCase()}` : '',
  ]
    .filter(Boolean)
    .join(', ');
  const filtersActive = allocation !== 'all' || Boolean(book || exchange || product || side || q);
  const emptyMessage = failed ? 'Fills unavailable.' : filtersActive ? 'No fills match these filters.' : 'No fills for this trade day.';
  const end = fills && fills.length > 0 ? page.start + fills.length - 1 : page.start;
  const listsLoading = !listsReady;
  const rangeLabel = total === 0 || !fills || fills.length === 0 ? `Showing 0 of ${total}` : `Showing ${page.start}–${end} of ${total}`;

  const rowTree = useMemo<HFDataTableRowTree<FillRow> | undefined>(() => {
    if (!grouped) return undefined;
    return {
      getChildren: (row) => (row.kind === 'group' ? row.children : undefined),
      expandedIds: expanded,
      onExpandedIdsChange: (ids) => setExpanded(new Set([...ids].map(String))),
      getToggleLabel: (row, open) => {
        if (row.id.startsWith('group:order:')) return `${open ? 'Collapse' : 'Expand'} order ${row.id.slice('group:order:'.length)}`;
        const name = row.tradeTime === 'No product' ? 'No product' : row.tradeTime;
        return `${open ? 'Collapse' : 'Expand'} product ${name}`;
      },
    };
  }, [grouped, expanded]);

  const clearFilters = () => {
    setAllocation('all');
    setBook('');
    setExchange('');
    setProduct('');
    setSide('');
    setSearch('');
    setQ('');
  };

  return (
    <HFCard
      component="section"
      aria-label="Trade blotter"
      aria-busy={fills === null}
      sx={{ ...monitorPaneSx, '& .MuiCardContent-root': { minHeight: 0, overflow: 'hidden' } }}
    >
      <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        {compact ? (
          <PanelHeader title="Trade Blotter fills" summary={summary} open={chromeOpen} onToggle={() => setChromeOpen((open) => !open)} />
        ) : null}
        {!compact || chromeOpen ? (
          <HFBox sx={CHROME_SX}>
            <HFBox sx={CHROME_ROW_SX}>
              {compact ? null : (
                <HFBox sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                  <HFBox component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Trade blotter
                  </HFBox>
                  {fills !== null ? (
                    <HFBox component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {total === 1 ? '1 fill' : `${total} fills`}
                    </HFBox>
                  ) : null}
                </HFBox>
              )}
              <HFBox sx={{ ...CLUSTER_SX, ml: 'auto' }}>
                <HFButton size="small" variant="outlined" sx={COMPACT_BUTTON_SX} onClick={openEditor} disabled={!layoutReady}>
                  Add/remove columns
                </HFButton>
                <HFButton
                  size="small"
                  variant="outlined"
                  sx={COMPACT_BUTTON_SX}
                  onClick={() => void exportCsv()}
                  loading={exporting}
                  disabled={fills === null}
                >
                  Export
                </HFButton>
              </HFBox>
            </HFBox>

            <HFBox sx={CHROME_ROW_SX}>
              <HFBox component="div" role="group" aria-label="Allocation" sx={CLUSTER_SX}>
                {FILTERS.map((item) => (
                  <HFButton
                    key={item.id}
                    size="small"
                    variant={allocation === item.id ? 'contained' : 'outlined'}
                    aria-pressed={allocation === item.id}
                    onClick={() => setAllocation(item.id)}
                    sx={COMPACT_BUTTON_SX}
                  >
                    {item.label}
                  </HFButton>
                ))}
              </HFBox>
              <HFBox component="div" role="group" aria-label="Group by" sx={CLUSTER_SX}>
                <HFBox component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                  GROUP BY
                </HFBox>
                {GROUPS.map((item) => (
                  <HFButton
                    key={item.id}
                    size="small"
                    variant={groupBy === item.id ? 'contained' : 'outlined'}
                    aria-pressed={groupBy === item.id}
                    onClick={() => chooseGroupBy(item.id)}
                    sx={COMPACT_BUTTON_SX}
                  >
                    {item.label}
                  </HFButton>
                ))}
                {groupBy !== 'none' ? (
                  <HFButton
                    size="small"
                    variant="text"
                    onClick={() => setExpanded(allExpanded ? new Set() : new Set(groupIds))}
                    sx={COMPACT_BUTTON_SX}
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </HFButton>
                ) : null}
              </HFBox>
            </HFBox>

            <HFBox sx={FILTER_GRID_SX}>
              <HFTextField
                label="Search"
                placeholder="Exec id, order id, symbol"
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 64 } }}
                sx={{ ...FILTER_FIELD_SX, width: '100%' }}
              />
              <HFSelect
                label="Book"
                size="small"
                displayEmpty
                disabled={listsLoading}
                value={listsLoading ? '' : book}
                onChange={setBook}
                options={facetOptions(listsLoading, facets.books, book)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Exchange"
                size="small"
                displayEmpty
                disabled={listsLoading}
                value={listsLoading ? '' : exchange}
                onChange={setExchange}
                options={facetOptions(listsLoading, facets.exchanges, exchange)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Product"
                size="small"
                displayEmpty
                disabled={listsLoading}
                value={listsLoading ? '' : product}
                onChange={setProduct}
                options={facetOptions(listsLoading, facets.products, product)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Side"
                size="small"
                displayEmpty
                value={side}
                onChange={setSide}
                options={[
                  { value: '', label: 'All' },
                  { value: 'BUY', label: 'BUY' },
                  { value: 'SELL', label: 'SELL' },
                ]}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFTooltip title={NOT_READY}>
                <HFBox component="span" sx={GRID_CELL_SX}>
                  <HFTextField
                    label="Trade date"
                    size="small"
                    disabled
                    value={formatDate(date ?? new Date().toISOString().slice(0, 10), 'medium')}
                    sx={{ ...FILTER_FIELD_SX, width: '100%' }}
                  />
                </HFBox>
              </HFTooltip>
              <DisabledSelect label="Strategy" title={NOT_READY} />
              <DisabledSelect label="Clearing account" title={NOT_READY} />
              <DisabledSelect label="Broker" title={NOT_READY} />
              <DisabledSelect label="Currency" title={NOT_READY} />
              <DisabledSelect label="Importer status" title={NOT_READY} />
              <HFButton
                size="small"
                variant="text"
                onClick={clearFilters}
                disabled={!filtersActive && search === ''}
                sx={{ ...COMPACT_BUTTON_SX, justifySelf: 'end' }}
              >
                Clear filters
              </HFButton>
            </HFBox>
          </HFBox>
        ) : null}

        <HFBox sx={{ position: 'relative', flex: 1, minHeight: 160 }}>
          {fills === null || !layoutReady ? (
            <TableSkeleton testId="trade-blotter-skeleton" />
          ) : (
            <HFBox sx={{ position: 'absolute', inset: 0, minHeight: 0, overflow: 'hidden' }}>
              <HFDataTable<FillRow>
                rows={rows}
                columns={gridColumns}
                columnVisibilityModel={visibility}
                getRowId={(row) => row.id}
                rowTree={rowTree}
                sortModel={sort ? [{ field: sort.id, sort: sort.direction }] : []}
                onSortModelChange={onSortModelChange}
                onColumnsReorder={onColumnsReorder}
                onColumnWidthChange={onColumnWidthChange}
                columnSearch={false}
                disableColumnMenu
                hideFooter
                fill
                autoHeight={false}
                autosizeOnMount={false}
                density="compact"
                rowHeight={32}
                loading={pageLoading}
                disableColumnVirtualization
                slots={{ noRowsOverlay: () => <EmptyFills message={emptyMessage} /> }}
              />
            </HFBox>
          )}
        </HFBox>

        {fills !== null && (!failed || pages.length > 1) ? (
          <HFBox
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderTop: 1,
              borderColor: 'divider',
              fontSize: 13,
              color: 'text.secondary',
            }}
          >
            <HFBox component="span">
              {`Total · ${total === 1 ? '1 fill' : `${total} fills`} · ${lotLine(boughtQty, soldQty, unsidedQty)}`}
              {marketValueTotal != null ? ` · Market value ${formatNumber(marketValueTotal, 2)}` : ''}
            </HFBox>
            <HFBox sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HFSelect
                label="Rows"
                size="small"
                value={pageSize}
                onChange={(value) => setPageSize(value as (typeof PAGE_SIZES)[number])}
                options={PAGE_SIZES.map((size) => ({ value: size, label: size }))}
                formControlSx={{ ...FILTER_FIELD_SX, width: 88 }}
              />
              {sort ? <HFBox component="span">Sorted on this page</HFBox> : null}
              <HFBox component="span">{rangeLabel}</HFBox>
              <HFButton
                size="small"
                variant="text"
                disabled={pages.length < 2}
                onClick={() => setPages((current) => current.slice(0, -1))}
                sx={COMPACT_BUTTON_SX}
              >
                Previous
              </HFButton>
              <HFButton
                size="small"
                variant="text"
                disabled={!nextCursor}
                onClick={() => {
                  if (!nextCursor || !fills) return;
                  setPages((current) => [...current, { cursor: nextCursor, start: page.start + fills.length }]);
                }}
                sx={COMPACT_BUTTON_SX}
              >
                Next
              </HFButton>
            </HFBox>
          </HFBox>
        ) : null}
      </HFBox>
      <HFModal
        open={editing}
        onClose={() => setEditing(false)}
        title="Add/remove columns"
        maxWidth="md"
        fullWidth
        dividers
        actions={
          <>
            <HFButton variant="outlined" onClick={() => setEditing(false)}>
              Cancel
            </HFButton>
            <HFButton onClick={applyColumns} disabled={sameColumns(draft, layoutColumns) || visibleDraft.length === 0}>
              Apply
            </HFButton>
          </>
        }
      >
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <HFBox sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>Columns</HFBox>
          <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>{`${visibleDraft.length} shown.`}</HFBox>
          <ColumnVisibilityList columns={draft} label={(id) => FILL_COLUMN_DETAILS[id as OmsFillColumnId].label} onChange={setDraft} />
        </HFBox>
      </HFModal>
    </HFCard>
  );
}
